import { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Grid2, useMediaQuery } from '@mui/material';
import CapacityIcon from 'assets/icons/capacity.svg';
import Layout from 'components/Layout';
import { useStyles } from './styles';
import { ExperienceScheduleForm } from './ExperienceScheduleForm';
import { useCalendarPage } from './hook';
import { PreviewService } from 'core/api/services/preview';
import { useExperiences } from 'core/contexts/experience';
import { useAuth } from 'core/contexts/auth';
import { ExperienceInfoService } from 'core/api/services/ExperienceInfo/experienceInfo';
import { CircularIndeterminate, CalendarPage as FullCalendar, PreviewPriceGroup, MoneyFormattedUtils } from '@utriper/core-ui';

const ExperienceCalendarPage = () => {
    const history = useHistory();
    const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { id } = useParams();
    const expId = parseInt(id);
    const classes = useStyles();
    const { getUserInfoSync } = useAuth();
    const { loading, experience, setExperience, experienceSchedule, setExperienceSchedule, getExperienceSchedule } = useExperiences();
    const [calendarRange, setCalendarRange] = useState({});
    const [experienceDetailId, setExperienceDetailId] = useState(null);
    const [experienceDetailOptions, setExperienceDetailOptions] = useState(null);

    const {
        date: { value, onChange },
        calendar: { start, end, range, ref, onSelect, onDismiss, onSave, tz, setStart, setEnd, setTz },
        popOver: { show, position, clickedEventValues, clickedSchedule },
        onEventClick,
    } = useCalendarPage();

    const getEvents = async () => {
        if (calendarRange?.start && calendarRange?.end && experienceDetailId) {
            await getExperienceSchedule(calendarRange, experienceDetailId);
        }
    };

    useEffect(() => {
        if (!expId) return;
        setExperience({});
        ExperienceInfoService.get(expId)
            .then(async (expData) => {
                const userData = await getUserInfoSync();
                if (!userData.experiencer || !userData.experiencer.id || expData.experiencerId !== userData.experiencer.id) {
                    throw new Error('Cannot edit another user experience!');
                }
                setExperience(expData);

                if (expData?.expDetail) {
                    const experienceDetails = expData.expDetail.map((expDetail) => ({ ...expDetail, name: expDetail?.description1 }));
                    setExperienceDetailId(experienceDetails[0]?.id);
                    setExperienceDetailOptions(experienceDetails);
                }
            })
            .catch((err) => {
                alert(err);
                history.replace('/home');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expId]);

    useEffect(() => {
        getEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experience, calendarRange, experienceDetailId]);

    const renderEventContent = (eventInfo) => {
        if (eventInfo.timeText.endsWith('- 23:59')) {
            eventInfo.timeText = eventInfo.timeText.replace('- 23:59', '- 00:00');
        } else if (eventInfo.timeText.endsWith('- 0')) {
            eventInfo.timeText = eventInfo.timeText.replace('- 0', '- 00:00');
        }

        const hasPriceGroup = Number.isInteger(eventInfo.event.extendedProps?.priceGroupId) && eventInfo.event.extendedProps?.priceGroupId > 0;

        return (
            <Grid2
                container
                flexDirection="column"
                flexWrap="nowrap"
                sx={{
                    padding: '6px',
                    flexDirection: 'column',
                    height: '100%',
                    overflowY: 'auto',
                    // Custom scrollbar styles
                    '&::-webkit-scrollbar': {
                        width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        borderRadius: '2px',
                    },
                    fontWeight: 600,
                    gap: '4px',
                }}
            >
                <Grid2>{hasPriceGroup ? <PreviewPriceGroup id={eventInfo.event.extendedProps.priceGroupId} label="name" previewService={PreviewService} styles={{ color: '#fff !important' }} route={`/price-group/${eventInfo.event.extendedProps.priceGroupId}`} /> : `${eventInfo.event.extendedProps?.currency} ${MoneyFormattedUtils(eventInfo.event.extendedProps?.price)}`}</Grid2>
                <Grid2>
                    <img src={CapacityIcon} alt="capacity" width={20} style={{ marginRight: 5 }} />
                    {eventInfo.event.extendedProps?.capacity} {eventInfo.event.extendedProps?.privateSlot ? 'priv' : ''}
                </Grid2>
                <Grid2>
                    <span>{eventInfo.timeText}</span>
                </Grid2>
            </Grid2>
        );
    };

    return (
        <Layout>
            <Grid2 container className={classes.gridPadre}>
                {loading && (
                    <div className={classes.loaderContainer}>
                        <div>
                            <CircularIndeterminate centered={true} />
                        </div>
                    </div>
                )}
                <FullCalendar useCalendarPage={useCalendarPage} schedules={experienceSchedule} renderEventContent={renderEventContent} setCalendarRange={setCalendarRange} value={value} onChange={onChange} title={experience.name} experienceDetail={experienceDetailId} setExperienceDetail={setExperienceDetailId} experienceDetailOptions={experienceDetailOptions} tz={tz} setTz={setTz} start={start} end={end} range={range} ref={ref} onSelect={onSelect} onDismiss={onDismiss} isSmall={isSmall} onEventClick={onEventClick} showSelectOptions>
                    <ExperienceScheduleForm
                        start={start}
                        end={end}
                        setStart={setStart}
                        setEnd={setEnd}
                        onSelect={onSelect}
                        range={range}
                        open={show}
                        onDismiss={onDismiss}
                        experienceSchedule={[...experienceSchedule]}
                        onSave={({ data, event }) => {
                            const events = [...experienceSchedule];
                            switch (event) {
                                case 'onSave':
                                    if (data.repeat) {
                                        getEvents();
                                    } else {
                                        events.push({
                                            id: data.id,
                                            experienceScheduleId: data.id,
                                            start: data.startUTC,
                                            end: data.endUTC,
                                            extendedProps: data,
                                        });
                                        setExperienceSchedule([...events]);
                                    }
                                    break;
                                case 'onUpdate':
                                    if (data.repeat && data.all) {
                                        getEvents();
                                    } else {
                                        const ev = events.find((e) => e.id === data.id);
                                        ev.start = data.startUTC;
                                        ev.end = data.endUTC;
                                        ev.extendedProps = data;
                                        setExperienceSchedule(events);
                                        if (data.until) {
                                            getEvents();
                                        }
                                    }
                                    break;
                                case 'onDelete':
                                    if (data.repeat && data.all) {
                                        getEvents();
                                    } else {
                                        const index = events.findIndex((e) => e.id === data.id);
                                        if (index !== -1) {
                                            events.splice(index, 1);
                                        }
                                        setExperienceSchedule(events);
                                    }
                                    break;
                                default:
                                    break;
                            }
                            onSave();
                        }}
                        position={position}
                        expId={experience.id}
                        subExpId={experienceDetailId}
                        clickedEventValues={clickedEventValues}
                        clickedSchedule={clickedSchedule}
                        getEvents={getEvents}
                        calendarTz={tz}
                    />
                </FullCalendar>
            </Grid2>
        </Layout>
    );
};

ExperienceCalendarPage.displayName = 'ExperienceCalendarPage';
export default ExperienceCalendarPage;
